import {
    Box, Paper, Table, TableBody, TableContainer,
    TableHead, Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import styles from '../../../styles.module.css';
import {
    Breadcrumb,
    CustomePopover,
    HandleSort,
    StyledTableCell,
    StyledTableRow,
} from "../../reusableComponent/Reusables";
import HeaderSection from "../../reusableComponent/HeaderSection";
import operatorMasterService from "../../../services/masters/operatorMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import CustomPagination from "../../reusableComponent/CustomPagination";
// import DeleteDialog from "../../reusableComponent/DeleteDialog"; // adjust path if needed

const OperatorList = () => {
    const login_details = sessionStorage.getItem('loginUser');
    const userD = JSON.parse(login_details);

    const [rows, setRows] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [sortBy, setSortBy] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [total_count, setTotalCount] = useState(0);

    const [controller, setController] = useState({ page: 0, rowsPerPage: 15 });

    // ── Excel export field map ───────────────────────────────────────────────
    const fields = ['operator_id', 'operator_name', 'plant_name'];
    const labels = ['ID', 'Operator Name', 'Plant'];

    // ── Pagination helpers ───────────────────────────────────────────────────
    useEffect(() => {
        setTotalCount(rows[0]?.totalCount ?? rows.length);
    }, [rows]);

    const handleChangePage = (event, newPage) => {
        setController(prev => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event) => {
        setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    // ── Fetch ────────────────────────────────────────────────────────────────
    const fetchData = async (paramsOverride = null) => {
        try {
            const params = paramsOverride || {
                page: controller.page,
                size: controller.rowsPerPage,
                searchKey,
            };
            const response = await operatorMasterService.getOperatorList(params);
            setRows(Array.isArray(response) ? response : []);
        } catch (error) {
            setSuccessMessage("Error fetching data");
        }
    };

    useEffect(() => { fetchData(); }, [controller]);

    // ── Search ───────────────────────────────────────────────────────────────
    const handleSearch = async (e) => {
        const value = e.target.value;
        setSearchKey(value);
        await fetchData({
            page: controller.page,
            size: value === '' ? controller.rowsPerPage : (rows[0]?.total_count || 0),
            searchKey: value,
        });
    };

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = (row) => {
        setIsDelete(true);
        setDeleteInfo(row);
    };

    const handleClose = () => {
        setIsDelete(false);
        setDeleteInfo(null);
    };

    const deleteRecord = async (id) => {
        try {
            await operatorMasterService.editDeleteOperator({
                operator_id: id,
                changedUpdatedValue: "delete",
            });
            setIsDelete(false);
            fetchData();
            setSuccessMessage("Record deleted successfully");
        } catch (error) {
            setSuccessMessage("Error deleting record");
        }
    };

    // ── Sorted rows ──────────────────────────────────────────────────────────
    const sortedRows = [...rows].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    const effectiveTotalCount = total_count > 0 ? total_count : rows.length;
    const startRecord = effectiveTotalCount === 0 ? 0 : controller.page * controller.rowsPerPage + 1;
    const endRecord = Math.min((controller.page + 1) * controller.rowsPerPage, effectiveTotalCount);
    const paginationInfo = `${startRecord}-${endRecord} of ${effectiveTotalCount}`;

    return (
        <Box className={`${styles.cts_page}`} sx={{ padding: "0px !important", margin: "0px !important" }}>
            <Box className={`${styles.body}`}>
                <Breadcrumb
                    routeSegments={[{ name: 'Operator List' }]}
                />

                <HeaderSection
                    title="Operator Master List"
                    addNewPath="/masters/add-operator"
                    searchKey={searchKey}
                    handleSearch={handleSearch}
                    sortedRows={sortedRows}
                    fields={fields}
                    labels={labels}
                    filename="Operator List"
                    paginationInfo={paginationInfo}
                    controller={controller}
                    handleChangePage={handleChangePage}
                />

                <Paper sx={{ width: '100%' }}>
                    <TableContainer component={Paper} className={`${styles.list_tbl_container}`}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell
                                        align="left"
                                        sx={{ width: '40%' }}
                                        className={`${styles.table_head}`}
                                        onClick={() => HandleSort('operator_name', sortBy, setSortBy, rows)}
                                    >
                                        Operator Name
                                        <span className={`${styles.sort_icon}`}>
                                            {sortBy === 'operator_name' ? ' ▲' : ' ▼'}
                                        </span>
                                    </StyledTableCell>

                                    <StyledTableCell
                                        align="left"
                                        sx={{ width: '40%' }}
                                        className={`${styles.table_head}`}
                                        onClick={() => HandleSort('plant_name', sortBy, setSortBy, rows)}
                                    >
                                        Plant
                                        <span className={`${styles.sort_icon}`}>
                                            {sortBy === 'plant_name' ? ' ▲' : ' ▼'}
                                        </span>
                                    </StyledTableCell>

                                    <StyledTableCell align="center" className={`${styles.table_head}`}>
                                        Action
                                    </StyledTableCell>
                                </StyledTableRow>
                            </TableHead>

                            <TableBody sx={{ backgroundColor: '#fff' }}>
                                {Array.isArray(sortedRows) && sortedRows.map((row, index) => (
                                    <StyledTableRow
                                        key={row.operator_id || index}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                                            {row.operator_name}
                                        </StyledTableCell>
                                        <StyledTableCell align="left" className={`${styles.list_table_body}`}>
                                            {row.plant_name}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <CustomePopover
                                                row={row}
                                                handleDelete={() => handleDelete(row)}
                                                handleCancel={false}
                                                linkTo="/masters/add-operator"
                                                handlePrint={false}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}

                                {sortedRows.length === 0 && (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={3} align="center">
                                            <Typography sx={{ fontSize: '13px', color: '#A0AEC0', py: 3 }}>
                                                No operators found.
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <CustomPagination
                        page={controller.page}
                        rowsPerPage={controller.rowsPerPage}
                        totalCount={total_count || 0}
                        onPageChange={(newPage) => handleChangePage(null, newPage)}
                    />
                </Paper>

                {/* <DeleteDialog
                    open={isDelete}
                    handleClose={handleClose}
                    deleteInfo={deleteInfo}
                    deleteRecord={deleteRecord}
                    deleteID={deleteInfo?.operator_id || null}
                    displayName={deleteInfo?.operator_name || ''}
                /> */}

                <ReusableSnackbar
                    open={!!successMessage}
                    onClose={(event, reason) => {
                        if (reason !== 'clickaway') setSuccessMessage('');
                    }}
                    message={successMessage}
                    autoHideDuration={2000}
                    vertical="top"
                    horizontal="right"
                />
            </Box>
        </Box>
    );
};

export default OperatorList;